// public/js/modules/map/rendering.js
import { setCurrentFilteredAssets, currentFilteredAssets } from './state.js';
import { addAssetMarker, clearMarkers, spreadCoLocatedMarkers } from './markers.js';
import { destroyClusterLayers, applyClustering } from './clustering.js';
import { mapFilterManager } from '../filters/filter-manager.js';
import { isValidLatLon, CLUSTER_MIN_ASSETS } from './constants.js';
import { renderTetherOverlay } from './tether-overlay.js';


export async function renderFilteredMarkers(assets, map, preserveViewport = false) {
  if (!map) return;

  // Adding sources/layers before the style has loaded throws "Style is not done
  // loading". We trust map.__styleReady (set in core.js on 'style.load'), because
  // isStyleLoaded() reports false for a moment even after the style IS ready.
  // The wait must be GENUINE: an old 2s "safety net" resolved while a slow style
  // was still loading, addSource threw, and the map stayed empty until the next
  // poll ("Error loading map data" on cold loads). Now we wait until the style
  // is actually ready (event + poll), with a generous 20s cap for dead networks.
  if (typeof map.isStyleLoaded === 'function' && !map.__styleReady && !map.isStyleLoaded()) {
    await new Promise(resolve => {
      let done = false;
      const finish = () => { if (done) return; done = true; clearTimeout(t); clearInterval(iv); resolve(); };
      const t = setTimeout(finish, 20000);
      const iv = setInterval(() => { if (map.__styleReady || map.isStyleLoaded()) finish(); }, 60);
      map.once('style.load', finish);
    });
  }

  let savedCenter = null;
  let savedZoom = null;

  if (preserveViewport) {
    savedCenter = map.getCenter();
    savedZoom = map.getZoom();
  }

  clearMarkers();
  destroyClusterLayers(map);
  // NOTE: deliberately do NOT clear the drawn route here. Marker re-renders
  // happen on every filter change AND on the periodic delta poll — wiping the
  // route made the open asset's trail vanish on each refresh. The route's
  // lifecycle belongs to the info panel (open / close / switch asset).

  setCurrentFilteredAssets(assets || []);

  const clusteringState = (mapFilterManager && mapFilterManager.getState)
    ? (mapFilterManager.getState().clustering || { enabled: true })
    : { enabled: true };

  try {
    if (clusteringState.enabled && currentFilteredAssets.length >= CLUSTER_MIN_ASSETS) {
      // Clustering path (GeoJSON) — we still want the individual markers cleared
      await applyClustering(map, currentFilteredAssets);
    } else {
      // Individual rich DOM markers
      currentFilteredAssets.forEach(asset => {
        if (isValidLatLon(asset.lastLat, asset.lastLon)) {
          addAssetMarker(asset, map);
        }
      });
      // Pins on the same pixel would hide one another entirely, so fan them
      // out - and re-do it on every zoom, since whether two pins collide is a
      // screen-distance question that changes with the camera.
      spreadCoLocatedMarkers(map);
      if (!map.__spreadOnZoom) {
        map.__spreadOnZoom = true;
        map.on('zoomend', () => spreadCoLocatedMarkers(map));
      }
    }
  } catch (e) {
    // A base-style switch can land between our readiness check and addSource.
    // Re-render once the new style is ready instead of leaving the map empty.
    if (String(e?.message || '').includes('Style is not done loading')) {
      console.warn('[Map] style changed mid-render — retrying after style.load');
      map.once('style.load', () => { renderFilteredMarkers(assets, map, true).catch(() => {}); });
      return;
    }
    throw e;
  }

  // Dashed connector for separated tethered beacons (defensive; never throws).
  renderTetherOverlay(currentFilteredAssets, map);

  if (!preserveViewport) {
    fitToFilteredAssets(map);
    console.log(`[Map] Auto-fit to ${currentFilteredAssets.length} assets`);
  } else if (savedCenter && savedZoom != null) {
    map.setCenter(savedCenter);
    map.setZoom(savedZoom);
    console.log(`[Map] Viewport preserved after filter change`);
  }

  console.log(`[Map] Rendered ${currentFilteredAssets.length} filtered assets`);
}

export function fitToFilteredAssets(map) {
  if (!map) return;

  if (!currentFilteredAssets || currentFilteredAssets.length === 0) {
    map.setCenter([8.2, 46.8]);
    map.setZoom(7);
    return;
  }

  const bounds = new maplibregl.LngLatBounds();
  let hasPosition = false;

  currentFilteredAssets.forEach(asset => {
    if (isValidLatLon(asset.lastLat, asset.lastLon)) {
      bounds.extend([parseFloat(asset.lastLon), parseFloat(asset.lastLat)]);
      hasPosition = true;
    }
  });

  if (hasPosition) {
    map.fitBounds(bounds, { padding: 80, maxZoom: 15, duration: 700 });
  }
}
